import { useState, useEffect } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import Layout from '../components/Layout';
import ConfirmationModal from '../components/ConfirmationModal';
import { useToast } from '../contexts/ToastContext';
import { accountService } from '../services/accountService';
import {
  privateMcpService,
  type PrivateMcpSalesforceApp,
  type PrivateMcpM365App,
  type PrivateMcpGrant,
  PrivateMcpGoogleApp,
  type PrivateMcpHubSpotApp,
} from '../services/privateMcpService';

const MCP_BASE = 'https://mcp.privacypal.ai';
const SALESFORCE_CONNECTOR_URL = `${MCP_BASE}/mcp/salesforce`;
const M365_CONNECTOR_URL = `${MCP_BASE}/mcp/m365`;
const M365_CALLBACK_URL = `${MCP_BASE}/oauth/upstream/m365/callback`;
const M365_PERMISSIONS = 'User.Read, Mail.ReadWrite, Mail.Send, People.Read, Files.ReadWrite, offline_access';
const GOOGLE_CONNECTOR_URL = `${MCP_BASE}/mcp/google`;
const GOOGLE_CALLBACK_URL = `${MCP_BASE}/oauth/upstream/google/callback`;
const GOOGLE_APIS = 'Gmail API, Google Drive API, People API';
const GOOGLE_SCOPES = 'gmail.modify, drive, contacts.readonly, contacts.other.readonly, userinfo.email, userinfo.profile, openid';
const HUBSPOT_CONNECTOR_URL = `${MCP_BASE}/mcp/hubspot`;
const HUBSPOT_CALLBACK_URL = `${MCP_BASE}/oauth/upstream/hubspot/callback`;

const inputStyle = {
  backgroundColor: 'var(--color-surface-secondary)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text)',
} as const;

function StatusBadge({ registered, loading }: { registered: boolean; loading: boolean }) {
  if (registered) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
        <Trans>Registered</Trans>
      </span>
    );
  }
  if (loading) return null;
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
      style={{ backgroundColor: 'var(--color-surface-secondary)', color: 'var(--color-text-muted)' }}
    >
      <Trans>Not configured</Trans>
    </span>
  );
}

function CopyUrlField({ url, copied, onCopy }: { url: string; copied: boolean; onCopy: () => void }) {
  const { t } = useLingui();
  return (
    <div className="flex gap-2 max-w-xl">
      <input type="text" value={url} readOnly className="flex-1 px-4 py-2 rounded-lg text-sm font-mono" style={inputStyle} />
      <button
        onClick={onCopy}
        className="p-2 rounded-lg transition-colors hover:bg-slate-100 dark:hover:bg-white/10"
        style={{ color: 'var(--color-text-muted)' }}
        title={t`Copy connector URL to clipboard`}
      >
        {copied ? (
          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
    </div>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`w-5 h-5 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
      style={{ color: 'var(--color-text-muted)' }}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

export default function PrivateMcpPage() {
  const { showToast } = useToast();
  const { t } = useLingui();

  // Friendly messages for the PUT validation codes returned by the API.
  const salesforceSaveErrorMessage = (code: string): string | null => {
    switch (code) {
      case 'invalid_salesforce_host':
        return t`That does not look like a Salesforce My Domain login URL. Use the form https://yourcompany.my.salesforce.com.`;
      case 'invalid_client_id':
        return t`That does not look like an External Client App consumer key. Copy it from the app's OAuth settings in Salesforce.`;
      case 'invalid_server_slug':
        return t`That is not a valid hosted MCP server slug. Use the platform/name form, for example platform/sobject-all.`;
      default:
        return null;
    }
  };

  const m365SaveErrorMessage = (code: string): string | null => {
    switch (code) {
      case 'invalid_tenant_id':
        return t`That does not look like a Directory (tenant) ID. Use the tenant GUID from the app registration overview, a verified domain such as contoso.onmicrosoft.com, or organizations.`;
      case 'invalid_client_id':
        return t`That does not look like an Application (client) ID. Copy the GUID from the app registration overview in Microsoft Entra.`;
      default:
        return null;
    }
  };

  const googleSaveErrorMessage = (code: string): string | null => {
    switch (code) {
      case 'invalid_client_id':
        return t`That does not look like a Google OAuth client ID. Copy the full value ending in .apps.googleusercontent.com from your Google Cloud credentials page.`;
      case 'invalid_client_secret':
        return t`That does not look like a Google OAuth client secret. Copy the value shown next to the client ID in Google Cloud.`;
      case 'invalid_workspace_domain':
        return t`Enter your Google Workspace domain on its own, such as example.com.`;
      case 'config_error':
        return t`The Private MCP credential vault is not configured on the server yet, so the client secret cannot be stored. Contact PrivacyPal support.`;
      default:
        return null;
    }
  };

  const hubspotSaveErrorMessage = (code: string): string | null => {
    switch (code) {
      case 'invalid_client_id':
        return t`That does not look like a HubSpot MCP connector client ID. Copy it from the connector's details page in HubSpot.`;
      case 'invalid_client_secret':
        return t`That does not look like a HubSpot client secret. Copy the value shown on the connector's details page.`;
      case 'invalid_hub_id':
        return t`Enter your HubSpot account ID as digits only, as shown in the account menu in HubSpot.`;
      case 'config_error':
        return t`The Private MCP credential vault is not configured on the server yet, so the client secret cannot be stored. Contact PrivacyPal support.`;
      default:
        return null;
    }
  };

  // The registry stores epoch milliseconds.
  const formatEpochMs = (ms?: number | null): string => {
    if (!ms) return t`unknown`;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return t`unknown`;
    return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const [isAdmin, setIsAdmin] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [salesforceExpanded, setSalesforceExpanded] = useState(false);
  const [m365Expanded, setM365Expanded] = useState(false);
  const [googleExpanded, setGoogleExpanded] = useState(false);
  const [hubspotExpanded, setHubspotExpanded] = useState(false);

  // Salesforce app registration state
  const [app, setApp] = useState<PrivateMcpSalesforceApp | null>(null);
  const [appLoading, setAppLoading] = useState(true);
  const [loginUrl, setLoginUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [serverSlug, setServerSlug] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showDeleteAppModal, setShowDeleteAppModal] = useState(false);

  // Microsoft 365 (Entra) app registration state
  const [m365App, setM365App] = useState<PrivateMcpM365App | null>(null);
  const [m365Loading, setM365Loading] = useState(true);
  const [tenantId, setTenantId] = useState('');
  const [m365ClientId, setM365ClientId] = useState('');
  const [m365Saving, setM365Saving] = useState(false);
  const [m365SaveError, setM365SaveError] = useState('');
  const [showDeleteM365Modal, setShowDeleteM365Modal] = useState(false);

  // Google Workspace (Google Cloud OAuth client) state. The secret is held
  // only in this form field until it is saved; it is never read back.
  const [googleApp, setGoogleApp] = useState<PrivateMcpGoogleApp | null>(null);
  const [googleLoading, setGoogleLoading] = useState(true);
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleClientSecret, setGoogleClientSecret] = useState('');
  const [googleWorkspaceDomain, setGoogleWorkspaceDomain] = useState('');
  const [googleSaving, setGoogleSaving] = useState(false);
  const [googleSaveError, setGoogleSaveError] = useState('');
  const [showDeleteGoogleModal, setShowDeleteGoogleModal] = useState(false);

  // HubSpot (MCP connector) state. Like Google, the secret is held only in
  // this form field until it is saved; it is never read back.
  const [hubspotApp, setHubspotApp] = useState<PrivateMcpHubSpotApp | null>(null);
  const [hubspotLoading, setHubspotLoading] = useState(true);
  const [hubspotClientId, setHubspotClientId] = useState('');
  const [hubspotClientSecret, setHubspotClientSecret] = useState('');
  const [hubspotHubId, setHubspotHubId] = useState('');
  const [hubspotSaving, setHubspotSaving] = useState(false);
  const [hubspotSaveError, setHubspotSaveError] = useState('');
  const [showDeleteHubspotModal, setShowDeleteHubspotModal] = useState(false);

  // Connected users (grants) state
  const [grants, setGrants] = useState<PrivateMcpGrant[]>([]);
  const [vaultReady, setVaultReady] = useState(true);
  const [grantsLoading, setGrantsLoading] = useState(false);
  const [grantsForbidden, setGrantsForbidden] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<PrivateMcpGrant | null>(null);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    loadAccount();
    loadApp();
    loadM365App();
    loadGoogleApp();
    loadHubspotApp();
  }, []);

  const loadAccount = async () => {
    const account = await accountService.getAccountStatus();
    const admin = !!account?.isAdmin;
    setIsAdmin(admin);
    if (admin) loadGrants();
  };

  const loadApp = async () => {
    try {
      setAppLoading(true);
      const data = await privateMcpService.getSalesforceApp();
      setApp(data);
      setLoginUrl(data?.loginUrl || '');
      setClientId(data?.clientId || '');
      setServerSlug(data?.serverSlug || '');
    } catch (error: any) {
      if (error.response?.status !== 403) {
        showToast(error.response?.data?.error || t`Failed to load the Salesforce app registration`, 'error');
      }
    } finally {
      setAppLoading(false);
    }
  };

  const loadM365App = async () => {
    try {
      setM365Loading(true);
      const data = await privateMcpService.getM365App();
      setM365App(data);
      setTenantId(data?.tenantId || '');
      setM365ClientId(data?.clientId || '');
    } catch (error: any) {
      if (error.response?.status !== 403) {
        showToast(error.response?.data?.error || t`Failed to load the Microsoft 365 app registration`, 'error');
      }
    } finally {
      setM365Loading(false);
    }
  };

  const loadGoogleApp = async () => {
    try {
      setGoogleLoading(true);
      const data = await privateMcpService.getGoogleApp();
      setGoogleApp(data);
      setGoogleClientId(data?.clientId || '');
      setGoogleWorkspaceDomain(data?.workspaceDomain || '');
      // Deliberately not populated from the server: the secret is write-only.
      setGoogleClientSecret('');
    } catch (error: any) {
      if (error.response?.status !== 403) {
        showToast(error.response?.data?.error || t`Failed to load the Google Workspace app registration`, 'error');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const loadHubspotApp = async () => {
    try {
      setHubspotLoading(true);
      const data = await privateMcpService.getHubSpotApp();
      setHubspotApp(data);
      setHubspotClientId(data?.clientId || '');
      setHubspotHubId(data?.hubId || '');
      // Deliberately not populated from the server: the secret is write-only.
      setHubspotClientSecret('');
    } catch (error: any) {
      if (error.response?.status !== 403) {
        showToast(error.response?.data?.error || t`Failed to load the HubSpot app registration`, 'error');
      }
    } finally {
      setHubspotLoading(false);
    }
  };

  const loadGrants = async () => {
    try {
      setGrantsLoading(true);
      const data = await privateMcpService.getGrants();
      setGrants(data.grants);
      setVaultReady(data.vaultReady);
      setGrantsForbidden(false);
    } catch (error: any) {
      if (error.response?.status === 403) {
        setGrantsForbidden(true);
      } else {
        showToast(error.response?.data?.error || t`Failed to load connected users`, 'error');
      }
    } finally {
      setGrantsLoading(false);
    }
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(url);
      showToast(t`Connector URL copied to clipboard`, 'success');
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const handleSaveApp = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const payload: { loginUrl: string; clientId: string; serverSlug?: string } = {
        loginUrl: loginUrl.trim(),
        clientId: clientId.trim(),
      };
      if (serverSlug.trim()) payload.serverSlug = serverSlug.trim();
      const saved = await privateMcpService.setSalesforceApp(payload);
      setApp(saved);
      setLoginUrl(saved.loginUrl || '');
      setClientId(saved.clientId || '');
      setServerSlug(saved.serverSlug || '');
      showToast(t`Salesforce app registration saved`, 'success');
    } catch (error: any) {
      const code = error.response?.data?.error;
      const message =
        salesforceSaveErrorMessage(code) ||
        error.response?.data?.message ||
        code ||
        t`Failed to save the app registration`;
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteApp = async () => {
    setShowDeleteAppModal(false);
    try {
      await privateMcpService.deleteSalesforceApp();
      setApp(null);
      setLoginUrl('');
      setClientId('');
      setServerSlug('');
      setSaveError('');
      showToast(t`Salesforce app registration removed`, 'success');
    } catch (error: any) {
      showToast(error.response?.data?.message || error.response?.data?.error || t`Failed to remove the app registration`, 'error');
    }
  };

  const handleSaveM365App = async () => {
    setM365Saving(true);
    setM365SaveError('');
    try {
      const saved = await privateMcpService.setM365App({ tenantId: tenantId.trim(), clientId: m365ClientId.trim() });
      setM365App(saved);
      setTenantId(saved.tenantId || '');
      setM365ClientId(saved.clientId || '');
      showToast(t`Microsoft 365 app registration saved`, 'success');
    } catch (error: any) {
      const code = error.response?.data?.error;
      const message =
        m365SaveErrorMessage(code) ||
        error.response?.data?.message ||
        code ||
        t`Failed to save the app registration`;
      setM365SaveError(message);
    } finally {
      setM365Saving(false);
    }
  };

  const handleDeleteM365App = async () => {
    setShowDeleteM365Modal(false);
    try {
      await privateMcpService.deleteM365App();
      setM365App(null);
      setTenantId('');
      setM365ClientId('');
      setM365SaveError('');
      showToast(t`Microsoft 365 app registration removed`, 'success');
    } catch (error: any) {
      showToast(error.response?.data?.message || error.response?.data?.error || t`Failed to remove the app registration`, 'error');
    }
  };

  const handleSaveGoogleApp = async () => {
    setGoogleSaving(true);
    setGoogleSaveError('');
    try {
      const payload: { clientId: string; clientSecret: string; workspaceDomain?: string } = {
        clientId: googleClientId.trim(),
        clientSecret: googleClientSecret.trim(),
      };
      if (googleWorkspaceDomain.trim()) payload.workspaceDomain = googleWorkspaceDomain.trim();
      const saved = await privateMcpService.setGoogleApp(payload);
      setGoogleApp(saved);
      setGoogleClientId(saved.clientId || '');
      setGoogleWorkspaceDomain(saved.workspaceDomain || '');
      // Clear the secret from the form as soon as it is stored.
      setGoogleClientSecret('');
      showToast(t`Google Workspace app registration saved`, 'success');
    } catch (error: any) {
      const code = error.response?.data?.error;
      const message =
        googleSaveErrorMessage(code) ||
        error.response?.data?.message ||
        code ||
        t`Failed to save the app registration`;
      setGoogleSaveError(message);
    } finally {
      setGoogleSaving(false);
    }
  };

  const handleDeleteGoogleApp = async () => {
    setShowDeleteGoogleModal(false);
    try {
      await privateMcpService.deleteGoogleApp();
      setGoogleApp(null);
      setGoogleClientId('');
      setGoogleClientSecret('');
      setGoogleWorkspaceDomain('');
      setGoogleSaveError('');
      showToast(t`Google Workspace app registration removed`, 'success');
    } catch (error: any) {
      showToast(error.response?.data?.message || error.response?.data?.error || t`Failed to remove the app registration`, 'error');
    }
  };

  const handleSaveHubspotApp = async () => {
    setHubspotSaving(true);
    setHubspotSaveError('');
    try {
      const payload: { clientId: string; clientSecret: string; hubId?: string } = {
        clientId: hubspotClientId.trim(),
        clientSecret: hubspotClientSecret.trim(),
      };
      if (hubspotHubId.trim()) payload.hubId = hubspotHubId.trim();
      const saved = await privateMcpService.setHubSpotApp(payload);
      setHubspotApp(saved);
      setHubspotClientId(saved.clientId || '');
      setHubspotHubId(saved.hubId || '');
      // Clear the secret from the form as soon as it is stored.
      setHubspotClientSecret('');
      showToast(t`HubSpot app registration saved`, 'success');
    } catch (error: any) {
      const code = error.response?.data?.error;
      const message =
        hubspotSaveErrorMessage(code) ||
        error.response?.data?.message ||
        code ||
        t`Failed to save the app registration`;
      setHubspotSaveError(message);
    } finally {
      setHubspotSaving(false);
    }
  };

  const handleDeleteHubspotApp = async () => {
    setShowDeleteHubspotModal(false);
    try {
      await privateMcpService.deleteHubSpotApp();
      setHubspotApp(null);
      setHubspotClientId('');
      setHubspotClientSecret('');
      setHubspotHubId('');
      setHubspotSaveError('');
      showToast(t`HubSpot app registration removed`, 'success');
    } catch (error: any) {
      showToast(error.response?.data?.message || error.response?.data?.error || t`Failed to remove the app registration`, 'error');
    }
  };

  const handleRevokeGrant = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await privateMcpService.revokeGrant(revokeTarget.connectorId, revokeTarget.userId);
      showToast(t`Connection revoked for ${revokeTarget.email || revokeTarget.userId}`, 'success');
      setRevokeTarget(null);
      await loadGrants();
    } catch (error: any) {
      showToast(error.response?.data?.error || t`Failed to revoke the connection`, 'error');
    } finally {
      setRevoking(false);
      setRevokeTarget(null);
    }
  };

  const revokeConnectorId = revokeTarget?.connectorId || '';
  const revokeWho = revokeTarget?.email || revokeTarget?.userId || t`this user`;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header + explainer (connector-agnostic) */}
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}><Trans>Private MCP</Trans></h1>
          <p className="text-sm mt-1 max-w-3xl" style={{ color: 'var(--color-text-secondary)' }}>
            <Trans>
              Private MCPs let your AI assistant work with your business systems while it only ever sees
              Privacy Twins. PrivacyPal translates between twins and real data at the system boundary.
              Each connector below is its own Private MCP.
            </Trans>
          </p>
        </div>

        {/* Connector cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          {/* Salesforce connector card */}
          <section
            className={`rounded-2xl border overflow-hidden ${salesforceExpanded ? 'md:col-span-2' : ''}`}
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}
          >
            <button
              type="button"
              onClick={() => setSalesforceExpanded((v) => !v)}
              className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.03]"
              aria-expanded={salesforceExpanded}
              aria-controls="salesforce-mcp-panel"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-sky-600 dark:text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--color-text)' }}>
                    Salesforce
                    <StatusBadge registered={!!app} loading={appLoading} />
                  </h2>
                  <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--color-text-muted)' }}>
                    {app ? (
                      <Trans>Twin-translating Salesforce access · last updated {formatEpochMs(app.updatedAt)}</Trans>
                    ) : (
                      <Trans>Connect your assistant to Salesforce through Privacy Twins</Trans>
                    )}
                  </p>
                </div>
              </div>
              <Chevron open={salesforceExpanded} />
            </button>

            {salesforceExpanded && (
              <div id="salesforce-mcp-panel" className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                {/* Connector URL */}
                <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
                  <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                    <Trans>Connector URL</Trans>
                  </p>
                  <p className="text-xs mb-3 max-w-3xl" style={{ color: 'var(--color-text-muted)' }}>
                    <Trans>
                      Point your AI assistant at this URL; PrivacyPal translates between twins and real
                      Salesforce data at the boundary.
                    </Trans>
                  </p>
                  <CopyUrlField url={SALESFORCE_CONNECTOR_URL} copied={copied === SALESFORCE_CONNECTOR_URL} onCopy={() => handleCopyUrl(SALESFORCE_CONNECTOR_URL)} />
                </div>

                {/* App registration */}
                <div className="px-5 py-4 space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}><Trans>App registration</Trans></h3>
                    <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      {app ? (
                        <Trans>Registered. Last updated {formatEpochMs(app.updatedAt)}</Trans>
                      ) : (
                        <Trans>Not configured yet</Trans>
                      )}
                    </p>
                  </div>

                  {appLoading ? (
                    <div className="flex justify-center py-8"><div className="spinner" /></div>
                  ) : isAdmin ? (
                    <>
                      <div className="space-y-3 max-w-xl">
                        <div>
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                            <Trans>My Domain login URL</Trans>
                          </label>
                          <input
                            type="text"
                            value={loginUrl}
                            onChange={(e) => setLoginUrl(e.target.value)}
                            placeholder="https://yourcompany.my.salesforce.com"
                            className="w-full px-4 py-2 rounded-lg text-sm"
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                            <Trans>Consumer Key</Trans>
                          </label>
                          <input
                            type="text"
                            value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                            placeholder={t`External Client App consumer key`}
                            className="w-full px-4 py-2 rounded-lg text-sm font-mono"
                            style={inputStyle}
                          />
                          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                            <Trans>This is a public client identifier, not a secret.</Trans>
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                            <Trans>Hosted MCP server <span className="font-normal opacity-70">(optional)</span></Trans>
                          </label>
                          <input
                            type="text"
                            value={serverSlug}
                            onChange={(e) => setServerSlug(e.target.value)}
                            placeholder="platform/sobject-all"
                            className="w-full px-4 py-2 rounded-lg text-sm font-mono"
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      {saveError && (
                        <p className="text-sm text-red-500">{saveError}</p>
                      )}

                      <div className="flex items-center gap-3">
                        <button
                          onClick={handleSaveApp}
                          disabled={saving || !loginUrl.trim() || !clientId.trim()}
                          className="px-4 py-2 text-sm font-semibold rounded-lg transition-colors bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                        >
                          {saving ? <Trans>Saving...</Trans> : app ? <Trans>Update registration</Trans> : <Trans>Save registration</Trans>}
                        </button>
                        {app && (
                          <button
                            onClick={() => setShowDeleteAppModal(true)}
                            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
                          >
                            <Trans>Remove registration</Trans>
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2 max-w-xl">
                      {app ? (
                        <dl className="space-y-2 text-sm">
                          <div className="flex justify-between gap-4">
                            <dt style={{ color: 'var(--color-text-muted)' }}><Trans>My Domain login URL</Trans></dt>
                            <dd className="font-mono truncate" style={{ color: 'var(--color-text)' }}>{app.loginUrl}</dd>
                          </div>
                          <div className="flex justify-between gap-4">
                            <dt style={{ color: 'var(--color-text-muted)' }}><Trans>Consumer Key</Trans></dt>
                            <dd className="font-mono truncate" style={{ color: 'var(--color-text)' }}>{app.clientId}</dd>
                          </div>
                          {app.serverSlug && (
                            <div className="flex justify-between gap-4">
                              <dt style={{ color: 'var(--color-text-muted)' }}><Trans>Hosted MCP server</Trans></dt>
                              <dd className="font-mono" style={{ color: 'var(--color-text)' }}>{app.serverSlug}</dd>
                            </div>
                          )}
                        </dl>
                      ) : (
                        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                          <Trans>No Salesforce app is registered yet. Ask an administrator to add one here.</Trans>
                        </p>
                      )}
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        <Trans>Only administrators can change this registration.</Trans>
                      </p>
                    </div>
                  )}

                  <p className="text-xs pt-2 border-t max-w-xl" style={{ color: 'var(--color-text-muted)', borderColor: 'var(--color-border)' }}>
                    <Trans>
                      Register an External Client App in your own Salesforce org with the OAuth scopes
                      {' '}<code className="font-mono">mcp_api</code> and <code className="font-mono">refresh_token</code>{' '}
                      and PKCE enabled. Only these public values are kept here; no secret is stored with PrivacyPal.
                    </Trans>
                  </p>
                </div>
              </div>
            )}
          </section>

          {/* Microsoft 365 connector card */}
          <section
            className={`rounded-2xl border overflow-hidden ${m365Expanded ? 'md:col-span-2' : ''}`}
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}
          >
            <button
              type="button"
              onClick={() => setM365Expanded((v) => !v)}
              className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.03]"
              aria-expanded={m365Expanded}
              aria-controls="m365-mcp-panel"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--color-text)' }}>
                    Microsoft 365
                    <StatusBadge registered={!!m365App} loading={m365Loading} />
                  </h2>
                  <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--color-text-muted)' }}>
                    {m365App ? (
                      <Trans>Twin-translating Microsoft 365 access · last updated {formatEpochMs(m365App.updatedAt)}</Trans>
                    ) : (
                      <Trans>Connect your assistant to Outlook mail and OneDrive files through Privacy Twins</Trans>
                    )}
                  </p>
                </div>
              </div>
              <Chevron open={m365Expanded} />
            </button>

            {m365Expanded && (
              <div id="m365-mcp-panel" className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                {/* Connector URL */}
                <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
                  <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                    <Trans>Connector URL</Trans>
                  </p>
                  <p className="text-xs mb-3 max-w-3xl" style={{ color: 'var(--color-text-muted)' }}>
                    <Trans>
                      Point your AI assistant at this URL; PrivacyPal translates between twins and real
                      Microsoft 365 data at the boundary. Mail sent and files written through it carry real values.
                    </Trans>
                  </p>
                  <CopyUrlField url={M365_CONNECTOR_URL} copied={copied === M365_CONNECTOR_URL} onCopy={() => handleCopyUrl(M365_CONNECTOR_URL)} />
                </div>

                {/* App registration */}
                <div className="px-5 py-4 space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}><Trans>App registration</Trans></h3>
                    <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      {m365App ? (
                        <Trans>Registered. Last updated {formatEpochMs(m365App.updatedAt)}</Trans>
                      ) : (
                        <Trans>Not configured yet</Trans>
                      )}
                    </p>
                  </div>

                  {m365Loading ? (
                    <div className="flex justify-center py-8"><div className="spinner" /></div>
                  ) : isAdmin ? (
                    <>
                      <div className="space-y-3 max-w-xl">
                        <div>
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                            <Trans>Directory (tenant) ID</Trans>
                          </label>
                          <input
                            type="text"
                            value={tenantId}
                            onChange={(e) => setTenantId(e.target.value)}
                            placeholder={t`Tenant GUID, or a verified domain such as contoso.onmicrosoft.com`}
                            className="w-full px-4 py-2 rounded-lg text-sm font-mono"
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                            <Trans>Application (client) ID</Trans>
                          </label>
                          <input
                            type="text"
                            value={m365ClientId}
                            onChange={(e) => setM365ClientId(e.target.value)}
                            placeholder="00000000-0000-0000-0000-000000000000"
                            className="w-full px-4 py-2 rounded-lg text-sm font-mono"
                            style={inputStyle}
                          />
                          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                            <Trans>This is a public client identifier, not a secret.</Trans>
                          </p>
                        </div>
                      </div>

                      {m365SaveError && (
                        <p className="text-sm text-red-500">{m365SaveError}</p>
                      )}

                      <div className="flex items-center gap-3">
                        <button
                          onClick={handleSaveM365App}
                          disabled={m365Saving || !tenantId.trim() || !m365ClientId.trim()}
                          className="px-4 py-2 text-sm font-semibold rounded-lg transition-colors bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                        >
                          {m365Saving ? <Trans>Saving...</Trans> : m365App ? <Trans>Update registration</Trans> : <Trans>Save registration</Trans>}
                        </button>
                        {m365App && (
                          <button
                            onClick={() => setShowDeleteM365Modal(true)}
                            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
                          >
                            <Trans>Remove registration</Trans>
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2 max-w-xl">
                      {m365App ? (
                        <dl className="space-y-2 text-sm">
                          <div className="flex justify-between gap-4">
                            <dt style={{ color: 'var(--color-text-muted)' }}><Trans>Directory (tenant) ID</Trans></dt>
                            <dd className="font-mono truncate" style={{ color: 'var(--color-text)' }}>{m365App.tenantId}</dd>
                          </div>
                          <div className="flex justify-between gap-4">
                            <dt style={{ color: 'var(--color-text-muted)' }}><Trans>Application (client) ID</Trans></dt>
                            <dd className="font-mono truncate" style={{ color: 'var(--color-text)' }}>{m365App.clientId}</dd>
                          </div>
                        </dl>
                      ) : (
                        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                          <Trans>No Microsoft 365 app is registered yet. Ask an administrator to add one here.</Trans>
                        </p>
                      )}
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        <Trans>Only administrators can change this registration.</Trans>
                      </p>
                    </div>
                  )}

                  <div className="text-xs pt-2 border-t max-w-xl space-y-2" style={{ color: 'var(--color-text-muted)', borderColor: 'var(--color-border)' }}>
                    <p>
                      <Trans>
                        Register an app in your own Microsoft Entra tenant as a public client: add the redirect URI below under
                        the "Mobile and desktop applications" platform, and grant the delegated Microsoft Graph permissions
                        {' '}<code className="font-mono">{M365_PERMISSIONS}</code> (admin consent if your tenant requires it).
                        Only these public values are kept here; no secret is stored with PrivacyPal.
                      </Trans>
                    </p>
                    <p className="font-mono break-all" style={{ color: 'var(--color-text-secondary)' }}>{M365_CALLBACK_URL}</p>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Google Workspace */}
          <section
            className={`rounded-2xl border overflow-hidden ${googleExpanded ? 'md:col-span-2' : ''}`}
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}
          >
            <button
              type="button"
              onClick={() => setGoogleExpanded((v) => !v)}
              className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.03]"
              aria-expanded={googleExpanded}
              aria-controls="google-mcp-panel"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H6.911a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--color-text)' }}>
                    Google Workspace
                    <StatusBadge registered={!!googleApp} loading={googleLoading} />
                  </h2>
                  <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--color-text-muted)' }}>
                    {googleApp ? (
                      <Trans>Twin-translating Gmail and Drive access · last updated {formatEpochMs(googleApp.updatedAt)}</Trans>
                    ) : (
                      <Trans>Connect your assistant to Gmail and Google Drive through Privacy Twins</Trans>
                    )}
                  </p>
                </div>
              </div>
              <Chevron open={googleExpanded} />
            </button>

            {googleExpanded && (
              <div id="google-mcp-panel" className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                {/* Connector URL */}
                <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
                  <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                    <Trans>Connector URL</Trans>
                  </p>
                  <p className="text-xs mb-3 max-w-3xl" style={{ color: 'var(--color-text-muted)' }}>
                    <Trans>
                      Point your AI assistant at this URL; PrivacyPal translates between twins and real
                      Google Workspace data at the boundary. Mail sent and files written through it carry real values.
                    </Trans>
                  </p>
                  <CopyUrlField url={GOOGLE_CONNECTOR_URL} copied={copied === GOOGLE_CONNECTOR_URL} onCopy={() => handleCopyUrl(GOOGLE_CONNECTOR_URL)} />
                </div>

                {/* App registration */}
                <div className="px-5 py-4 space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}><Trans>OAuth client</Trans></h3>
                    <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      {googleApp ? (
                        <Trans>Registered. Last updated {formatEpochMs(googleApp.updatedAt)}</Trans>
                      ) : (
                        <Trans>Not configured yet</Trans>
                      )}
                    </p>
                  </div>

                  {googleLoading ? (
                    <div className="flex justify-center py-8"><div className="spinner" /></div>
                  ) : isAdmin ? (
                    <>
                      <div className="space-y-3 max-w-xl">
                        <div>
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                            <Trans>OAuth client ID</Trans>
                          </label>
                          <input
                            type="text"
                            value={googleClientId}
                            onChange={(e) => setGoogleClientId(e.target.value)}
                            placeholder="000000000000-xxxxxxxx.apps.googleusercontent.com"
                            className="w-full px-4 py-2 rounded-lg text-sm font-mono"
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                            <Trans>OAuth client secret</Trans>
                          </label>
                          <input
                            type="password"
                            autoComplete="new-password"
                            value={googleClientSecret}
                            onChange={(e) => setGoogleClientSecret(e.target.value)}
                            placeholder={googleApp?.hasClientSecret ? t`Stored. Enter a new secret only to replace it.` : 'GOCSPX-...'}
                            className="w-full px-4 py-2 rounded-lg text-sm font-mono"
                            style={inputStyle}
                          />
                          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                            <Trans>
                              Google does not offer a secretless client for a hosted callback, so this connector needs one.
                              It is encrypted before storage and never shown again, here or anywhere else.
                            </Trans>
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                            <Trans>Workspace domain (optional)</Trans>
                          </label>
                          <input
                            type="text"
                            value={googleWorkspaceDomain}
                            onChange={(e) => setGoogleWorkspaceDomain(e.target.value)}
                            placeholder="example.com"
                            className="w-full px-4 py-2 rounded-lg text-sm font-mono"
                            style={inputStyle}
                          />
                          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                            <Trans>Set this and only accounts in that Google Workspace can connect; a personal account is refused.</Trans>
                          </p>
                        </div>
                      </div>

                      {googleSaveError && (
                        <p className="text-sm text-red-500">{googleSaveError}</p>
                      )}

                      <div className="flex items-center gap-3">
                        <button
                          onClick={handleSaveGoogleApp}
                          disabled={googleSaving || !googleClientId.trim() || !googleClientSecret.trim()}
                          className="px-4 py-2 text-sm font-semibold rounded-lg transition-colors bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                        >
                          {googleSaving ? <Trans>Saving...</Trans> : googleApp ? <Trans>Update registration</Trans> : <Trans>Save registration</Trans>}
                        </button>
                        {googleApp && (
                          <button
                            onClick={() => setShowDeleteGoogleModal(true)}
                            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
                          >
                            <Trans>Remove registration</Trans>
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2 max-w-xl">
                      {googleApp ? (
                        <dl className="space-y-2 text-sm">
                          <div className="flex justify-between gap-4">
                            <dt style={{ color: 'var(--color-text-muted)' }}><Trans>OAuth client ID</Trans></dt>
                            <dd className="font-mono truncate" style={{ color: 'var(--color-text)' }}>{googleApp.clientId}</dd>
                          </div>
                          <div className="flex justify-between gap-4">
                            <dt style={{ color: 'var(--color-text-muted)' }}><Trans>Workspace domain</Trans></dt>
                            <dd className="font-mono truncate" style={{ color: 'var(--color-text)' }}>
                              {googleApp.workspaceDomain || t`any`}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-4">
                            <dt style={{ color: 'var(--color-text-muted)' }}><Trans>Client secret</Trans></dt>
                            <dd style={{ color: 'var(--color-text)' }}>
                              {googleApp.hasClientSecret ? <Trans>Stored</Trans> : <Trans>Missing</Trans>}
                            </dd>
                          </div>
                        </dl>
                      ) : (
                        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                          <Trans>No Google Workspace app is registered yet. Ask an administrator to add one here.</Trans>
                        </p>
                      )}
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        <Trans>Only administrators can change this registration.</Trans>
                      </p>
                    </div>
                  )}

                  <div className="text-xs pt-2 border-t max-w-xl space-y-2" style={{ color: 'var(--color-text-muted)', borderColor: 'var(--color-border)' }}>
                    <p>
                      <Trans>
                        In your own Google Cloud project, enable {GOOGLE_APIS}, set the OAuth consent screen
                        user type to "Internal", and create an OAuth client of type "Web application" with the
                        redirect URI below. Request the scopes{' '}<code className="font-mono">{GOOGLE_SCOPES}</code>.
                        Internal apps skip Google's verification review, and their refresh tokens do not expire
                        after seven days the way an external app still in testing does.
                      </Trans>
                    </p>
                    <p className="font-mono break-all" style={{ color: 'var(--color-text-secondary)' }}>{GOOGLE_CALLBACK_URL}</p>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* HubSpot */}
          <section
            className={`rounded-2xl border overflow-hidden ${hubspotExpanded ? 'md:col-span-2' : ''}`}
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}
          >
            <button
              type="button"
              onClick={() => setHubspotExpanded((v) => !v)}
              className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.03]"
              aria-expanded={hubspotExpanded}
              aria-controls="hubspot-mcp-panel"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="15" cy="14" r="3.75" strokeWidth={1.5} />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10.25V5.5m0 0a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM12.1 11.7L6.5 7.5m0 0a1.5 1.5 0 10-1.8-2.4 1.5 1.5 0 001.8 2.4zM12.4 16.8l-3.1 2.7" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--color-text)' }}>
                    HubSpot
                    <StatusBadge registered={!!hubspotApp} loading={hubspotLoading} />
                  </h2>
                  <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--color-text-muted)' }}>
                    {hubspotApp ? (
                      <Trans>Twin-translating HubSpot CRM access · last updated {formatEpochMs(hubspotApp.updatedAt)}</Trans>
                    ) : (
                      <Trans>Connect your assistant to HubSpot's MCP server through Privacy Twins</Trans>
                    )}
                  </p>
                </div>
              </div>
              <Chevron open={hubspotExpanded} />
            </button>

            {hubspotExpanded && (
              <div id="hubspot-mcp-panel" className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                {/* Connector URL */}
                <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
                  <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                    <Trans>Connector URL</Trans>
                  </p>
                  <p className="text-xs mb-3 max-w-3xl" style={{ color: 'var(--color-text-muted)' }}>
                    <Trans>
                      Point your AI assistant at this URL instead of HubSpot's own MCP server. PrivacyPal fronts
                      HubSpot's MCP server and translates between twins and real CRM data at the boundary. Records
                      created and updated through it carry real values, and HubSpot's write confirmations reach you
                      with twins in place of real values.
                    </Trans>
                  </p>
                  <CopyUrlField url={HUBSPOT_CONNECTOR_URL} copied={copied === HUBSPOT_CONNECTOR_URL} onCopy={() => handleCopyUrl(HUBSPOT_CONNECTOR_URL)} />
                </div>

                {/* App registration */}
                <div className="px-5 py-4 space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}><Trans>MCP connector</Trans></h3>
                    <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      {hubspotApp ? (
                        <Trans>Registered. Last updated {formatEpochMs(hubspotApp.updatedAt)}</Trans>
                      ) : (
                        <Trans>Not configured yet</Trans>
                      )}
                    </p>
                  </div>

                  {hubspotLoading ? (
                    <div className="flex justify-center py-8"><div className="spinner" /></div>
                  ) : isAdmin ? (
                    <>
                      <div className="space-y-3 max-w-xl">
                        <div>
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                            <Trans>Client ID</Trans>
                          </label>
                          <input
                            type="text"
                            value={hubspotClientId}
                            onChange={(e) => setHubspotClientId(e.target.value)}
                            placeholder="00000000-0000-0000-0000-000000000000"
                            className="w-full px-4 py-2 rounded-lg text-sm font-mono"
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                            <Trans>Client secret</Trans>
                          </label>
                          <input
                            type="password"
                            autoComplete="new-password"
                            value={hubspotClientSecret}
                            onChange={(e) => setHubspotClientSecret(e.target.value)}
                            placeholder={hubspotApp?.hasClientSecret ? t`Stored. Enter a new secret only to replace it.` : '00000000-0000-0000-0000-000000000000'}
                            className="w-full px-4 py-2 rounded-lg text-sm font-mono"
                            style={inputStyle}
                          />
                          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                            <Trans>
                              HubSpot's MCP server requires the client secret together with PKCE, so this connector needs one.
                              It is encrypted before storage and never shown again, here or anywhere else.
                            </Trans>
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                            <Trans>HubSpot account ID (optional)</Trans>
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={hubspotHubId}
                            onChange={(e) => setHubspotHubId(e.target.value)}
                            placeholder="12345678"
                            className="w-full px-4 py-2 rounded-lg text-sm font-mono"
                            style={inputStyle}
                          />
                          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                            <Trans>Set this and only that HubSpot account can be connected; a user who picks a different account is refused.</Trans>
                          </p>
                        </div>
                      </div>

                      {hubspotSaveError && (
                        <p className="text-sm text-red-500">{hubspotSaveError}</p>
                      )}

                      <div className="flex items-center gap-3">
                        <button
                          onClick={handleSaveHubspotApp}
                          disabled={hubspotSaving || !hubspotClientId.trim() || !hubspotClientSecret.trim()}
                          className="px-4 py-2 text-sm font-semibold rounded-lg transition-colors bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                        >
                          {hubspotSaving ? <Trans>Saving...</Trans> : hubspotApp ? <Trans>Update registration</Trans> : <Trans>Save registration</Trans>}
                        </button>
                        {hubspotApp && (
                          <button
                            onClick={() => setShowDeleteHubspotModal(true)}
                            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
                          >
                            <Trans>Remove registration</Trans>
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2 max-w-xl">
                      {hubspotApp ? (
                        <dl className="space-y-2 text-sm">
                          <div className="flex justify-between gap-4">
                            <dt style={{ color: 'var(--color-text-muted)' }}><Trans>Client ID</Trans></dt>
                            <dd className="font-mono truncate" style={{ color: 'var(--color-text)' }}>{hubspotApp.clientId}</dd>
                          </div>
                          <div className="flex justify-between gap-4">
                            <dt style={{ color: 'var(--color-text-muted)' }}><Trans>HubSpot account ID</Trans></dt>
                            <dd className="font-mono truncate" style={{ color: 'var(--color-text)' }}>
                              {hubspotApp.hubId || t`any`}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-4">
                            <dt style={{ color: 'var(--color-text-muted)' }}><Trans>Client secret</Trans></dt>
                            <dd style={{ color: 'var(--color-text)' }}>
                              {hubspotApp.hasClientSecret ? <Trans>Stored</Trans> : <Trans>Missing</Trans>}
                            </dd>
                          </div>
                        </dl>
                      ) : (
                        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                          <Trans>No HubSpot MCP connector is registered yet. Ask an administrator to add one here.</Trans>
                        </p>
                      )}
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        <Trans>Only administrators can change this registration.</Trans>
                      </p>
                    </div>
                  )}

                  <div className="text-xs pt-2 border-t max-w-xl space-y-2" style={{ color: 'var(--color-text-muted)', borderColor: 'var(--color-border)' }}>
                    <p>
                      <Trans>
                        In HubSpot, go to Development, then MCP Connectors, and create an MCP connector with the
                        redirect URL below. Copy its client ID and client secret here. There are no scopes to choose:
                        HubSpot grants the connector what its MCP tools need, limited by each user's own HubSpot
                        permissions, and every user approves it once when they connect.
                      </Trans>
                    </p>
                    <p className="font-mono break-all" style={{ color: 'var(--color-text-secondary)' }}>{HUBSPOT_CALLBACK_URL}</p>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Add new Private MCP (disabled placeholder) */}
          <div
            className="rounded-2xl border-2 border-dashed px-5 py-8 flex flex-col items-center justify-center text-center gap-2 select-none opacity-60 cursor-not-allowed"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}
            role="button"
            aria-disabled="true"
            title={t`More Private MCP connectors are coming soon`}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'var(--color-surface-secondary)' }}
            >
              <svg className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </div>
            <p className="text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
              <Trans>Add new Private MCP</Trans>
            </p>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              <Trans>More connectors coming soon</Trans>
            </p>
          </div>
        </div>

        {/* Connected users (admin only), across every connector */}
        {isAdmin && !grantsForbidden && (
          <section
            className="rounded-2xl border overflow-hidden"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}><Trans>Connected users</Trans></h3>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    <Trans>Who has connected their assistant through this Private MCP. Metadata only; tokens never leave the vault.</Trans>
                  </p>
                </div>
              </div>
              <button
                onClick={loadGrants}
                disabled={grantsLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-50"
                style={{ color: 'var(--color-text-muted)' }}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <Trans>Refresh</Trans>
              </button>
            </div>

            {!vaultReady && (
              <div className="mx-5 mb-4 rounded-xl border px-4 py-3 text-sm bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-800/40 text-amber-700 dark:text-amber-400">
                <Trans>The Private MCP vault key is not configured on the server; connections cannot be stored.</Trans>
              </div>
            )}

            {grantsLoading ? (
              <div className="flex justify-center py-10"><div className="spinner" /></div>
            ) : grants.length === 0 ? (
              <div className="px-5 pb-8 pt-2 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
                <Trans>No one has connected yet.</Trans>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                      <th className="text-left px-5 py-3 text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--color-text-muted)' }}><Trans>User</Trans></th>
                      <th className="text-left px-5 py-3 text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--color-text-muted)' }}><Trans>Connector</Trans></th>
                      <th className="text-left px-5 py-3 text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--color-text-muted)' }}><Trans>Host</Trans></th>
                      <th className="text-left px-5 py-3 text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--color-text-muted)' }}><Trans>Last updated</Trans></th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                    {grants.map((grant) => (
                      <tr key={`${grant.connectorId}:${grant.userId}`} className="hover:bg-slate-50 dark:hover:bg-white/[0.02]">
                        <td className="px-5 py-3 font-medium" style={{ color: 'var(--color-text)' }}>
                          {grant.email || grant.userId}
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-surface-secondary)', color: 'var(--color-text-muted)' }}>
                            {grant.connectorId}
                          </span>
                        </td>
                        <td className="px-5 py-3" style={{ color: 'var(--color-text-secondary)' }}>
                          {grant.instanceHost || t`unknown`}
                        </td>
                        <td className="px-5 py-3" style={{ color: 'var(--color-text-secondary)' }}>
                          {formatEpochMs(grant.updatedAt)}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={() => setRevokeTarget(grant)}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
                          >
                            <Trans>Revoke</Trans>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {isAdmin && grantsForbidden && (
          <div className="px-5 py-4 text-sm rounded-2xl border" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}>
            <Trans>Connected users are visible to administrators only.</Trans>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteAppModal}
        title={t`Remove app registration`}
        message={t`Remove the Salesforce External Client App registration for this workspace? Users will not be able to connect their assistants until an administrator registers an app again.`}
        confirmText={t`Remove`}
        variant="danger"
        onConfirm={handleDeleteApp}
        onCancel={() => setShowDeleteAppModal(false)}
      />

      <ConfirmationModal
        isOpen={showDeleteM365Modal}
        title={t`Remove app registration`}
        message={t`Remove the Microsoft Entra app registration for this workspace? Users will not be able to connect their assistants to Microsoft 365 until an administrator registers an app again.`}
        confirmText={t`Remove`}
        variant="danger"
        onConfirm={handleDeleteM365App}
        onCancel={() => setShowDeleteM365Modal(false)}
      />

      <ConfirmationModal
        isOpen={showDeleteGoogleModal}
        title={t`Remove app registration`}
        message={t`Remove the Google OAuth client for this workspace? The stored client secret is deleted with it, and users will not be able to connect their assistants to Gmail or Drive until an administrator registers a client again.`}
        confirmText={t`Remove`}
        variant="danger"
        onConfirm={handleDeleteGoogleApp}
        onCancel={() => setShowDeleteGoogleModal(false)}
      />

      <ConfirmationModal
        isOpen={showDeleteHubspotModal}
        title={t`Remove app registration`}
        message={t`Remove the HubSpot MCP connector for this workspace? The stored client secret is deleted with it, and users will not be able to connect their assistants to HubSpot until an administrator registers a connector again.`}
        confirmText={t`Remove`}
        variant="danger"
        onConfirm={handleDeleteHubspotApp}
        onCancel={() => setShowDeleteHubspotModal(false)}
      />

      <ConfirmationModal
        isOpen={!!revokeTarget && !revoking}
        title={t`Revoke connection`}
        message={t`Revoke the ${revokeConnectorId} connection for ${revokeWho}? Their assistant will need to reconnect.`}
        confirmText={t`Revoke`}
        variant="danger"
        onConfirm={handleRevokeGrant}
        onCancel={() => setRevokeTarget(null)}
      />
    </Layout>
  );
}
